/* eslint formatjs/no-literal-string-in-jsx: "error" */

import React, { FormEvent, useEffect, useState } from "react"
import { Body, Button, Container, FlexContainer, H2, Stack, Text, TextLink } from "@carrotfertility/carotene"
import { IlloSearching } from "../../../../components/views/atoms/Illustrations"
import { useGetLocationDetails } from "../../hooks/useGetLocationDetails"
import { LocationPicker } from "../location-picker/LocationPicker"
import { useUserContext } from "../../context/UserContext"
import {
  getProviderFinderSearchURL,
  ProviderFinderParam,
  providerFinderRootPath,
  RedirectToProviderFinderURL
} from "../../utils/providerFinderSearchUrl"
import { ProviderTypeFilterDropdown } from "./ProviderTypeFilterDropdown"
import { Redirect, useHistory } from "react-router"
import { getAvailableParentIdsByBenefitConfig, filterParentIdByBenefitConfig } from "../../utils/parentIdMapping"
import { useBroadAreaContext } from "../../context/BroadAreaContext"
import { FormattedMessage, useIntl } from "react-intl"
import { Benefit, ProgramType } from "pages/provider-finder/types/Benefit"

export function ProviderSearchForm({ benefit }: { benefit: Benefit }): JSX.Element {
  const intl = useIntl()

  const [locationPickerValue, setLocationPickerValue] = useState("")
  const [searchLocation, setSearchLocation] = useState("")
  const { data: locationDetails, isFetching, isError } = useGetLocationDetails(searchLocation)
  const { companyCountryCode } = useUserContext()
  const { setBroadAreaSearched } = useBroadAreaContext()

  const availableParentIds = getAvailableParentIdsByBenefitConfig(benefit)

  const history = useHistory()
  const params = new URLSearchParams(history.location.search)
  const providerTypeFromParams = params.get(ProviderFinderParam.ProviderType)
    ? params
        .get(ProviderFinderParam.ProviderType)
        .split(",")
        .filter((param) => availableParentIds.includes(param))
    : []
  const singleProviderType = availableParentIds.length === 1 ? availableParentIds : []
  const [providerType, setProviderType] = useState(
    (singleProviderType.length && singleProviderType) || providerTypeFromParams
  )

  useEffect(() => {
    setBroadAreaSearched(locationDetails?.isBroadArea)
  }, [setBroadAreaSearched, locationDetails?.isBroadArea])

  const isSearchDetailsLoaded = searchLocation && !isFetching && !isError
  if (isSearchDetailsLoaded) {
    const searchURL = getProviderFinderSearchURL({
      searchLocation,
      locationDetails,
      companyCountryCode,
      providerType
    })

    return <RedirectToProviderFinderURL url={searchURL} />
  }

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()
    setProviderType(providerType.filter(filterParentIdByBenefitConfig(benefit)).sort())
    setSearchLocation(locationPickerValue)
  }

  if (isError) {
    return <Redirect to={`${providerFinderRootPath}/error`} />
  }

  const HeadingText = (): JSX.Element => {
    if (availableParentIds.length === 1) {
      return <FormattedMessage defaultMessage="Where are you looking for a provider?" />
    }
    return <FormattedMessage defaultMessage="What are you looking for?" />
  }

  const SubHeadingText = (): JSX.Element => {
    if (benefit.program.type === ProgramType.CORE) {
      return (
        <Text color="black60">
          <FormattedMessage defaultMessage="All providers have been vetted by our experts to ensure the highest standards." />
        </Text>
      )
    } else {
      return (
        <Text color="black60">
          <FormattedMessage
            defaultMessage={
              "All providers have been vetted by our experts to ensure the highest standards. " +
              "You can only use Carrot funds for <link>eligible care and services</link> with these providers."
            }
            values={{
              link: (linkContent) => (
                <TextLink size="medium" color="black60" to="/your-benefit-guide/coverage">
                  {linkContent}
                </TextLink>
              )
            }}
          />
        </Text>
      )
    }
  }

  return (
    <FlexContainer
      background="lightBlue"
      padding={["72px 30px", "72px 30px", "72px 30px", "72px"]}
      borderRadius="8px"
      style={{ position: "relative" }}
    >
      <Container
        maxWidth={["0", "0", "0", "360px"]}
        minWidth={["0", "0", "0", "360px"]}
        style={{ position: "absolute", top: "28px", right: "32px", zIndex: 1 }}
      >
        <IlloSearching />
      </Container>
      <Container
        height="100%"
        width="100%"
        maxWidth="780px"
        borderRadius="8px"
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          zIndex: 2,
          background: "linear-gradient(90deg, #E0E8EA 60%, rgba(224, 232, 234, 0) 100%)"
        }}
      />
      <Container maxWidth={["auto", "auto", "auto", "540px"]} style={{ zIndex: 3 }}>
        <Stack spacing="tiny">
          <H2 style={{ fontSize: "32px" }}>
            <HeadingText />
          </H2>
          <Body style={{ marginBottom: "32px" }}>
            <SubHeadingText />
          </Body>
        </Stack>
        <form name="providerSearchForm" onSubmit={handleSubmit}>
          <Stack spacing={"small"}>
            <LocationPicker
              onInputChange={setLocationPickerValue}
              placeholderText={intl.formatMessage({ defaultMessage: "Address, city, state, or zip code" })}
            />
            {availableParentIds.length > 1 && (
              <ProviderTypeFilterDropdown
                providerType={providerType}
                setProviderType={setProviderType}
                benefit={benefit}
              />
            )}
            <Button type="submit" style={{ marginBottom: "40px" }} disabled={!locationPickerValue}>
              <FormattedMessage defaultMessage="Search" />
            </Button>
          </Stack>
        </form>
      </Container>
    </FlexContainer>
  )
}
